/**
 * TensionEngine.js
 * Calculates harmonic tension and maps it to musical parameters.
 * Used to modulate intensity, velocity, and dissonance.
 */

// Interval Dissonance Map (Semitones)
const DISSONANCE_SCORES = {
    0: 0,   // Unison
    1: 1.0, // Minor 2nd (High)
    2: 0.2, // Major 2nd
    3: 0.1, // Minor 3rd
    4: 0,   // Major 3rd
    5: 0,   // Perfect 4th
    6: 1.0, // Tritone (High)
    7: 0,   // Perfect 5th
    8: 0.3, // Minor 6th
    9: 0.1, // Major 6th
    10: 0.2, // Minor 7th
    11: 0.8  // Major 7th (High-ish)
};

// Configuration Constants
const NON_DIATONIC_WEIGHT = 0.25;
const TENSION_DIMINISHED = 0.3;
const TENSION_DOM7 = 0.2;
const TENSION_AUGMENTED = 0.4;

const DYNAMICS_THRESHOLD = 0.1;
const VELOCITY_BOOST_FACTOR = 0.3;
const DEFAULT_VELOCITY = 0.7;
const JITTER_AMOUNT = 0.05;

/**
 * Calculates the inherent tension of a chord within a scale context.
 * @param {Object} chord - The chord object { rootPitchClass, type, notes }.
 * @param {Array} scale - Array of pitch classes in the key.
 * @param {number} beatStrength - Optional (0.0 to 1.0). Dissonance on strong beats counts more.
 * @param {Object} context - Optional context { config }.
 * @returns {number} - Tension score 0.0 to 1.0.
 */
export const calculateTension = (chord, scale, beatStrength = 1.0, context = {}) => {
    if (!chord) return 0;

    let score = 0;
    const chordNotes = chord.notes || [];
    const pcs = chordNotes.map(n => n % 12);

    // 1. Non-Diatonic Tones (Chromaticism)
    // Count how many notes are NOT in the scale
    let nonDiatonicCount = 0;
    pcs.forEach(pc => {
        if (!scale.includes(pc)) nonDiatonicCount++;
    });
    score += (nonDiatonicCount * NON_DIATONIC_WEIGHT); // Each out-note adds tension

    // 2. Interval Dissonance (Crunch)
    // Check every pair of notes in the voicing
    let dissonanceSum = 0;
    let pairs = 0;
    for (let i = 0; i < pcs.length; i++) {
        for (let j = i + 1; j < pcs.length; j++) {
            const interval = Math.abs(pcs[i] - pcs[j]) % 12;
            const dScore = DISSONANCE_SCORES[interval] || 0;

            dissonanceSum += dScore;
            pairs++;
        }
    }

    if (pairs > 0) {
        let dissonance = (dissonanceSum / pairs);
        // Scale dissonance by Metric Strength
        // Passing tones (weak beats) are less "tense" than downbeat clashes.
        dissonance *= (0.5 + (beatStrength * 0.5));
        score += dissonance;
    }

    // 3. Functional Tension
    // Dominant chords (V) have tension. Diminished have more.
    if (chord.quality === 'diminished') score += TENSION_DIMINISHED;
    if (chord.type === 'dom7') score += TENSION_DOM7;
    if (chord.type === 'aug') score += TENSION_AUGMENTED;

    // Context Weighting (Task 2)
    if (context.config && context.config.vectors) {
        const harmonicComplexity = context.config.vectors.harmonicComplexity || 0.5;
        const rhythmicDensity = context.config.vectors.rhythmicDensity || 0.5;

        // Boost tension if harmonic complexity is high (Dissonance expected)
        if (harmonicComplexity > 0.8) {
            score *= 1.1;
        }

        // Dampen tension if rhythmic density is low (Space/Release)
        if (rhythmicDensity < 0.2) {
            score *= 0.8;
        }
    }

    return Math.min(1.0, score);
};

/**
 * Modifies events based on tension level.
 * @param {Array} events
 * @param {number} tension - 0.0 to 1.0
 * @param {number} sensitivity - 0.0 to 1.0 (Instrument resistance to global tension)
 */
export const applyTensionToDynamics = (events, tension, sensitivity = 1.0) => {
    // If sensitivity is 0, ignore tension completely ("Stubborn" instrument)
    if (!events || (tension * sensitivity) < DYNAMICS_THRESHOLD) return events;

    return events.map(ev => {
        // Boost velocity (Weighted by Sensitivity)
        // Formula: Base + (Tension * Sensitivity * Factor)
        const velBoost = tension * sensitivity * VELOCITY_BOOST_FACTOR;
        let newVel = (ev.vel || DEFAULT_VELOCITY) + velBoost;

        // Add Micro-timing jitter (anxiety) - also scaled by sensitivity
        const jitter = (Math.random() - 0.5) * (tension * sensitivity * JITTER_AMOUNT);

        return {
            ...ev,
            vel: Math.min(1.0, newVel),
            s: ev.s + jitter
        };
    });
};
